import React, { useMemo } from "react";
import { CategoriesModal, FaqModal, ImageDetailsModal } from "../modals";
import { MoreInfoModal } from "../modals";
import { StoreInterface } from "@/assets/interfaces";
import { ModalsState } from "@/components/hooks/useModals";
import { useSidemenu } from "@/components/hooks/useSidemenu";
import Portal from "@/components/portal";
import { StoreFrontLogo } from "./header";
import StorefrontContext, { VisitRoute } from "@/contexts/storefront";
import SideMenu from "@/components/ui/side-menu";
import { useRouter } from "next/router";
import AboutStoreModal from "../modals/store-pages/about-us";
import { AboutImagesModal, DeliveryFeesModal, RefundPolicyModal } from "../modals";
import { socialsDataMap } from "@/components/storefronts-v1/modals/details";
import { getWhatsappLink } from "@/assets/js/utils/utils";

interface Props {
  show: boolean;
  store: StoreInterface;
  modals: ModalsState<
    | "about_store"
    | "delivery_fees"
    | "refund_policy"
    | "more_info"
    | "image_details"
    | "faq"
    | "categories"
    | "about_images"
  >;
  toggle: (key: string) => void;
  mode?: "light" | "dark";
  formatAsCurrency: (amount: number) => string;
  toggleSidemenu: (key: string) => void;
  showContactUs?: boolean;
}

const StoreSideNav: React.FC<Props> = ({
  show,
  store,
  modals,
  toggle,
  mode,
  formatAsCurrency,
  toggleSidemenu,
  showContactUs = false,
}) => {
  const router = useRouter();
  const { getPaths } = StorefrontContext.useContainer();

  const computedPaths = useMemo(() => getPaths(store), [store]);

  const renderModalButtons = [
    { key: "home", label: "Home", show: true, href: computedPaths.home },
    { key: "products", label: "Shop", show: true, href: computedPaths.products },
    { key: "about_store", label: "About us", show: store?.about_us?.content },
    {
      key: "contact_us",
      label: "Contact us",
      show: showContactUs,
      href: getWhatsappLink(
        store?.phone,
        store.configuration.enquiry_message ?? `Hi, i'll like to make an enquiry about ${store.name}`
      ),
      openInNewTab: true,
    },
    { key: "categories", label: "Categories", show: store?.categories?.length > 0 },
    { key: "more_info", label: "More Information", show: true },
    { key: "faq", label: "Frequently Asked Questions", show: store?.faqs?.length > 0 },
  ];

  const socialsData = Object.entries(store?.socials ?? {})
    .filter((e) => !!e?.[1])
    .map((e) => ({ type: e[0], handle: e[1] }));

  const getImageModal = () => {
    toggle("more_info");
    toggle("image_details");
  };

  const getAboutImages = () => {
    toggle("about_store");
    toggle("about_images");
  };

  return (
    <Portal>
      <SideMenu
        show={show}
        toggle={() => toggleSidemenu("sidebar")}
        size="lg"
        headerAddon={<StoreFrontLogo logo={store?.logo} storeName={store?.name} />}
      >
        <div className="flex flex-col h-[88%] justify-between">
          <div className=" flex flex-col">
            {renderModalButtons.map(
              ({ key, label, show, href, openInNewTab = false }) =>
                show && (
                  <>
                    {href ? (
                      <a
                        key={key}
                        href={href}
                        target={openInNewTab ? "_blank" : undefined}
                        rel="noreferrer"
                        className="inline-flex items-center gap-x-2 py-3 text-base md:font-base font-bold font-display text-black-secondary no-underline"
                      >
                        {label}
                      </a>
                    ) : (
                      <button
                        key={key}
                        onClick={() => (href ? router.push(href) : toggle(key))}
                        className="inline-flex items-center gap-x-2 py-3 text-base md:font-base font-bold font-display text-black-secondary"
                      >
                        {label}
                      </button>
                    )}
                  </>
                )
            )}
          </div>

          <div className="flex w-full items-start justify-between gap-5 md:gap-6 flex-col pt-6.25 mt-6.25">
            <div className="flex items-center gap-4">
              {socialsData.map((s, index) => (
                <a
                  className="flex items-center text-black-muted"
                  key={index}
                  href={`${socialsDataMap[s.type].prefix}/${s.handle}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <figure className={`h-5 w-5 sm:h-5 sm:w-5 rounded-full flex items-center bg-grey-fields-100 py-1.5`}>
                    {socialsDataMap[s.type].icon}
                  </figure>
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center">
                <span
                  className={`text-1xs font-bold font-display inline-block pr-0.5 mr-0.5 ${
                    mode === "light" ? "text-black" : "text-white"
                  }`}
                >
                  &copy; {new Date().getFullYear()}
                </span>
                <span
                  className={`text-1xs font-bold font-display inline-block ${
                    mode === "light" ? "text-black" : "text-white"
                  }`}
                >
                  - {store?.name}
                </span>
              </div>
              <a
                href={process.env.NEXT_PUBLIC_DASHBOARD_URL}
                target="_blank"
                rel="noreferrer"
                className="bg-grey-fields-100 max-sm:hidden px-3.75 py-1.5 rounded-[100px] text-1xs text-black flex items-center font-medium space-x-1.5"
              >
                <span>Powered by</span>
                <img src="/images/logos/primary.svg" alt="Catlog logo" className="h-4" />
              </a>
            </div>
          </div>
        </div>
      </SideMenu>

      {store?.about_us?.content && (
        <AboutStoreModal
          storeName={store?.name}
          show={modals?.about_store}
          toggle={(state: boolean) => toggle("about_store")}
          store={store}
          getImageModal={() => getAboutImages()}
        />
      )}
      {store?.about_us?.images && store?.about_us?.images.length > 0 && (
        <AboutImagesModal
          images={store?.about_us?.images ?? []}
          title={`Brand Images`}
          show={modals?.about_images}
          toggle={(state: boolean) => toggle("about_images")}
        />
      )}
      <DeliveryFeesModal
        store={store}
        show={modals?.delivery_fees}
        toggle={(state: boolean) => toggle("delivery_fees")}
        formatCurrency={(amount: number) => formatAsCurrency(amount)}
      />
      <RefundPolicyModal
        store={store}
        show={modals?.refund_policy}
        toggle={(state: boolean) => toggle("refund_policy")}
      />
      <MoreInfoModal
        store={store}
        show={modals?.more_info}
        toggle={(state: boolean) => toggle("more_info")}
        getImageModal={() => getImageModal()}
      />
      <ImageDetailsModal
        images={store?.extra_info?.images ?? []}
        title={`${store?.extra_info?.images_label} Images`}
        show={modals?.image_details}
        toggle={(state: boolean) => toggle("image_details")}
      />
      {store?.faqs?.length > 0 && (
        <FaqModal show={modals?.faq} store={store} toggle={(state: boolean) => toggle("faq")} faqs={store?.faqs} />
      )}
      <CategoriesModal store={store} show={modals?.categories} toggle={(state: boolean) => toggle("categories")} />
    </Portal>
  );
};

export default StoreSideNav;
